import store from '@/store'
import { selectActiveLanguage } from '@/store/app'
import { i18n_en_us } from './i18n_en_us'
import { format, parseISO, formatDistance as dateFnsFormatDistance } from 'date-fns'
import { ptBR as localePtBr } from 'date-fns/locale/pt-BR'

let i18n_pt_br = null
let ptBrLoadingPromise = null
let currentStore = null
let warnedMissingKeys = new Set()
let warnedPtLoading = false

export function setI18nStore(nextStore) {
  if (nextStore && typeof nextStore.getState === 'function') {
    currentStore = nextStore
  }
}

const getI18nStore = () => currentStore || store

const isDev = () => {
  try {
    return Boolean(import.meta.env?.DEV)
  } catch {
    return process.env.NODE_ENV !== 'production'
  }
}

const warnMissingKey = (key, locale) => {
  if (!isDev()) return
  const id = `${locale}:${key}`
  if (warnedMissingKeys.has(id)) return
  warnedMissingKeys.add(id)
  const localeLabel = locale === 'pt-br' ? 'Portuguese (pt-BR)' : 'English'
  console.warn(
    `[i18n] Missing ${localeLabel} key: "${key}"\n` +
      `        Add it to src/locale/i18n_${locale === 'pt-br' ? 'pt_br' : 'en_us'}.js`,
  )
}

const warnPtStillLoading = () => {
  if (!isDev() || warnedPtLoading) return
  warnedPtLoading = true
  console.info(
    '[i18n] Portuguese dictionary is still loading — showing English briefly. UI will refresh when ready.',
  )
}

const bumpI18nVersion = () => {
  const activeStore = getI18nStore()
  if (!activeStore?.dispatch) return
  // Avoid circular imports — RTK action type from createSlice('app')
  activeStore.dispatch({ type: 'app/bumpI18nVersion' })
}

export const loadPortugueseTranslations = () => {
  if (i18n_pt_br !== null) {
    return Promise.resolve(i18n_pt_br)
  }
  if (ptBrLoadingPromise === null) {
    ptBrLoadingPromise = import('./i18n_pt_br')
      .then(module => {
        i18n_pt_br = module.i18n_pt_br
        ptBrLoadingPromise = null
        warnedPtLoading = false
        bumpI18nVersion()
        return i18n_pt_br
      })
      .catch(err => {
        ptBrLoadingPromise = null
        if (isDev()) {
          console.error('[i18n] Failed to load Portuguese translations', err)
        }
        throw err
      })
  }
  return ptBrLoadingPromise
}

const i18nObj = {
  en_us: i18n_en_us,
  get pt_br() {
    return i18n_pt_br
  },
}

export function getLocale() {
  const activeStore = getI18nStore()
  if (!activeStore) return 'en-US'
  const state = activeStore.getState()
  const language = selectActiveLanguage()(state)
  if (language === 'pt-br' && i18n_pt_br === null) {
    loadPortugueseTranslations()
  }
  return language === 'pt-br' ? 'pt-BR' : 'en-US'
}

/** Subscribe in components so strings re-evaluate when language or PT dictionary changes. */
export function selectI18nRevision(state) {
  return `${selectActiveLanguage()(state)}:${state.app?.i18nVersion ?? 0}`
}

export function i18n(key, kwargs = {}) {
  const activeStore = getI18nStore()
  if (!activeStore) return null
  const state = activeStore.getState()
  const l = selectActiveLanguage()(state)
  let obj
  let lookupLocale = l === 'pt-br' ? 'pt-br' : 'en'

  if (l === 'pt-br') {
    if (i18n_pt_br === null) {
      loadPortugueseTranslations()
      warnPtStillLoading()
      obj = i18nObj.en_us
      lookupLocale = 'en'
    } else {
      obj = i18n_pt_br
    }
  } else {
    obj = i18nObj.en_us
  }

  const str = key.split('.').reduce((o, i) => (o == null ? undefined : o[i]), obj)
  if (str == null) {
    warnMissingKey(key, lookupLocale === 'en' && l === 'pt-br' && i18n_pt_br ? 'pt-br' : lookupLocale)
    // If PT is loaded but key missing, try EN so UI is readable instead of raw key
    if (l === 'pt-br' && i18n_pt_br !== null) {
      const enStr = key.split('.').reduce((o, i) => (o == null ? undefined : o[i]), i18nObj.en_us)
      if (enStr != null) {
        if (enStr instanceof Array) return enStr
        return new Function(`return \`${enStr}\`;`).call(kwargs)
      }
    }
    return key
  }
  if (str instanceof Array) {
    return str
  }
  return new Function(`return \`${str}\`;`).call(kwargs)
}

export function formatDate(date, dateFormat) {
  const locale = getLocale()
  const getDateFormat = () => {
    if (locale === 'pt-BR') {
      return "dd 'de' MMMM, y 'às' HH'h'mm"
    }
    return "MMMM dd, y 'at' HH':'mm"
  }

  let formatString = dateFormat || getDateFormat()
  let parsedDate
  if (typeof date === 'string') {
    parsedDate = parseISO(date)
  } else {
    parsedDate = date
  }

  if (!parsedDate) return null
  if (locale === 'pt-BR') {
    return format(parsedDate, formatString, { locale: localePtBr })
  } else {
    return format(parsedDate, formatString)
  }
}

export function formatDistance(date1, date2) {
  const locale = getLocale()
  if (locale === 'pt-BR') {
    return dateFnsFormatDistance(date1, date2, { locale: localePtBr })
  } else {
    return dateFnsFormatDistance(date1, date2)
  }
}

export function formatNumber(value, options = {}) {
  const locale = options.locale || getLocale()
  const { minimumFractionDigits = 0, maximumFractionDigits = 2, roundDown = false, roundingMode = 'floor' } = options

  if (value == null) {
    return '-'
  }

  const formattedValue = roundDown ? roundDownSignificantDigits(value, maximumFractionDigits) : value
  return new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
    roundingMode,
  }).format(formattedValue)
}

export function formatPercentage(value, fixed = 2, suffix = '%') {
  const locale = getLocale()
  if (value == null) {
    return '-'
  }
  return new Intl.NumberFormat(locale).format(`${(100 * value).toFixed(fixed)}`) + suffix
}

const roundDownSignificantDigits = (number, decimals) => {
  let significantDigits = parseInt(number.toExponential().split('e-')[1]) || 0
  let decimalsUpdated = (decimals || 0) + significantDigits
  decimals = Math.min(decimalsUpdated, number.toString().length)
  return Math.floor(number * Math.pow(10, decimals)) / Math.pow(10, decimals)
}

export function getSeparators() {
  const locale = getLocale()
  const numberWithDecimalSeparator = 1.1
  const formattedNumber = numberWithDecimalSeparator.toLocaleString(locale)
  const decimalSeparator = formattedNumber.indexOf('.') >= 0 ? '.' : ','
  const thousandsSeparator = decimalSeparator === '.' ? ',' : '.'
  return { decimalSeparator, thousandsSeparator }
}

export function normalizeNumericStr(numberStr, separator) {
  const separators = separator || getSeparators()
  const numberWithoutThousands = numberStr.replace(new RegExp('\\' + separators.thousandsSeparator, 'g'), '')
  const numberString = numberWithoutThousands.replace(separators.decimalSeparator, '.')
  return numberString.trim()
}
